import { lazy, useContext, useEffect, useState, Suspense } from "react";
import { io } from "socket.io-client";
import { actions, urls } from "../dispatch";
import { dispatch } from "../dispatch/dispatch";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ContextStore from "../hooks/ContextStore";
import SignupPage from "../pages/Signup";
import UiPaths from "../paths/uiPaths";
import ProductGroupPage from "../pages/ProductGroup";
import SmartAnalyticsPage from "../pages/SmartAnalytics.page";
import StockIntelligencePage from "../pages/StockIntelligence.page";
import InventoryControlPage from "../pages/InventoryControl.page";
import SkuAssociationPage from "../pages/SkuAssociation.page";
import PricingIntelligencePage from "../pages/PricingIntelligence.page";
import AdminUsersPage from "../pages/AdminUsers.page";
import BuyingDecisionsPage from "../pages/BuyingDecisions.page";
import MarjanPage from "../pages/Marjan.page";
import MarjanSkuAssociationPage from "../pages/MarjanSkuAssociation.page";
import ProductListingPage from "../pages/ProductListing.page";
import ReturnsPage from "../pages/Returns.page";
import ListingOpportunitiesPage from "../pages/ListingOpportunities.page";
import WeeklyReportPage from "../pages/WeeklyReport.page";
import SupplyPlanPage from "../pages/SupplyPlan.page";
import SupportTicketsPage from "../pages/SupportTickets.page";
import PromotionsPage from "../pages/Promotions.page";
import PartnerStatementsPage from "../pages/PartnerStatements.page";
import MyTasksPage from "../pages/MyTasks.page";
import SystemHealthPage from "../pages/SystemHealth.page";
import TelegramScoutPage from "../pages/TelegramScout.page";
import ProductRadarPage from "../pages/ProductRadar.page";
import NetProfitPage from "../pages/NetProfit.page";
import WarehousePage from "../pages/Warehouse.page";
import BundlesStudioPage from "../pages/BundlesStudio.page";
import SizesPage from "../pages/Sizes.page";
import PhotoBankPage from "../pages/PhotoBank.page";
import OrdersTodayPage from "../pages/OrdersToday.page";
import ShoppingListPage from "../pages/ShoppingList.page";
import ScannerPage from "../pages/Scanner.page";
import DailyShipmentsPage from "../pages/DailyShipments.page";
import MyStorePage from "../pages/MyStore.page";
import ForgotPasswordPage from "../pages/ForgotPassword.page";
import ResetPasswordPage from "../pages/ResetPassword.page";
import { AppShell } from "../components/layout/AppShell";
import { PAGE_ROUTES, PAGES } from "../pages/registry";
import OrderAlerts from "../components/common/OrderAlerts";
import PhoneTables from "../components/common/PhoneTables";

const DashboardPage = lazy(() => import("../pages/DashBoard"));
const LoginPage     = lazy(() => import("../pages/Login"));

const Spinner = () => (
    <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
        Loading…
    </div>
);

const NoAccess = () => (
    <div className="flex flex-col h-full items-center justify-center gap-3 text-muted-foreground">
        <div style={{ fontSize: '2rem' }}>🔒</div>
        <p className="font-medium text-foreground">Access restricted</p>
        <p className="text-sm">You don't have permission to view this page.</p>
        <p className="text-xs">Contact the admin to request access.</p>
    </div>
);


// One component per page key of src/pages/registry.ts. A page in the registry without a
// component here (or the other way round) is reported in the console at start-up.
const PAGE_COMPONENTS: Record<string, JSX.Element> = {
    'smart-analytics':        <SmartAnalyticsPage />,
    'net-profit':             <NetProfitPage />,
    'bundles-studio':         <BundlesStudioPage />,
    'sizes':                  <SizesPage />,
    'photo-bank':             <PhotoBankPage />,
    'orders-today':           <OrdersTodayPage />,
    'shopping-list':          <ShoppingListPage />,
    'supply-plan':            <SupplyPlanPage />,
    'support-tickets':        <SupportTicketsPage />,
    'decisions':              <WeeklyReportPage />,
    'promotions':             <PromotionsPage />,
    'partner-statements':     <PartnerStatementsPage />,
    'my-tasks':               <MyTasksPage />,
    'system-health':          <SystemHealthPage />,
    'telegram-scout':         <TelegramScoutPage />,
    'buying-decisions':       <BuyingDecisionsPage />,
    'stock-intelligence':     <StockIntelligencePage />,
    'pricing-intelligence':   <PricingIntelligencePage />,
    'inventory-control':      <InventoryControlPage />,
    'warehouse':              <WarehousePage />,
    'daily-shipments':        <DailyShipmentsPage />,
    'my-store':               <MyStorePage />,
    'sku-association':        <SkuAssociationPage />,
    'product-radar':          <ProductRadarPage />,
    'listing':                <ProductListingPage />,
    'listing-opportunities':  <ListingOpportunitiesPage />,
    'returns':                <ReturnsPage />,
    'scanner':                <ScannerPage />,
    'marjan':                 <MarjanPage />,
    'marjan-sku-association': <MarjanSkuAssociationPage />,
    'dashboard':              <DashboardPage />,
    'productgroup':           <ProductGroupPage />,
};
for (const r of PAGE_ROUTES) if (!PAGE_COMPONENTS[r.key]) console.error(`[pages] "${r.key}" is in the registry but has no component in RouteProvider`);
for (const k of Object.keys(PAGE_COMPONENTS)) if (!PAGES.some(p => p.key === k)) console.error(`[pages] "${k}" has a component but is missing from src/pages/registry.ts (no sidebar entry, no permission)`);

const readToken = () => { try { return localStorage.getItem("token") || ""; } catch { return ""; } };

const RouteProvider = () => {
    const { contextStore, setContextStore } = useContext(ContextStore);
    const user = contextStore.user;

    // Session restore (2026-10-06): the token survives in localStorage but the user object
    // lived only in memory, so EVERY page refresh showed the login page — unusable on the
    // warehouse phones now that the Scanner lives here. Fetch the user with the saved token
    // first; only a rejected token leads to the login page.
    const [restoring, setRestoring] = useState<boolean>(() => !contextStore.user?._id && !!readToken());
    useEffect(() => {
        if (!restoring) return;
        const token = readToken();
        (async () => {
            const u: any = token ? await dispatch(actions.getUser, {}, token) : null;
            if (u && !("error" in u) && u._id) {
                const socket = io(urls.baseUrlSocketApi, { reconnectionDelayMax: 10000, auth: { token } });
                setContextStore((past: any) => ({ ...past, user: u, token, socket }));
            } else {
                try { localStorage.removeItem("token"); } catch {}
            }
            setRestoring(false);
        })();
    }, [restoring, setContextStore]);

    const { Login, Signup, ForgotPassword, ResetPassword, AdminUsers, SmartAnalytics } = UiPaths;

    // Permission helper
    const can = (key: string): boolean => {
        if (!user?._id) return false;
        if (user.type === 'admin') return true;
        if (!user.permissions?.length) return false;
        if (user.permissions.includes('all')) return true;
        return user.permissions.includes(key);
    };

    const guard = (key: string, el: JSX.Element) =>
        can(key) ? el : <NoAccess />;

    // First page the user can actually access (sidebar order, from the registry)
    const ORDERED_ROUTES = PAGES.map(p => ({ key: p.key, path: p.path }));
    const defaultPath =
        user.type === 'admin'
            ? SmartAnalytics
            : ORDERED_ROUTES.find(r => can(r.key))?.path ?? SmartAnalytics;

    if (restoring) return <div className="flex h-screen items-center justify-center text-muted-foreground text-sm">Loading…</div>;

    return (
        <BrowserRouter>
            {!contextStore.user._id ? (
                <Routes>
                    <Route path={Login}          element={<LoginPage />} />
                    <Route path={Signup}         element={<SignupPage />} />
                    <Route path={ForgotPassword} element={<ForgotPasswordPage />} />
                    <Route path={ResetPassword}  element={<ResetPasswordPage />} />
                    <Route path="/*"             element={<Navigate to={Login} />} />
                </Routes>
            ) : (
                <AppShell>
                    <OrderAlerts token={contextStore.token || readToken()} />
                    <PhoneTables />
                    <Suspense fallback={<Spinner />}>
                        <Routes>
                            {PAGE_ROUTES.map(r => <Route key={r.route} path={r.route} element={guard(r.key, PAGE_COMPONENTS[r.key] ?? <NoAccess />)} />)}
                            <Route path={AdminUsers}         element={user.type === 'admin' ? <AdminUsersPage /> : <NoAccess />} />
                            <Route path="/*"                 element={<Navigate to={defaultPath} replace />} />
                        </Routes>
                    </Suspense>
                </AppShell>
            )}
        </BrowserRouter>
    );
};

export default RouteProvider;
